"use client";

import * as React from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Map } from "lucide-react";
import TrailMap from "@/components/article-shell/TrailMap";
import {
  MobileBottomToolRegistration,
  useMobileBottomTools,
  type MobileBottomToolItem,
} from "@/components/mobile-bottom-tools";
import { getTrailMapLabel, TRAIL_QUERY_PARAM } from "@/lib/article-trail";

interface ArticleMobileMapToolProps {
  enabled: boolean;
}

export default function ArticleMobileMapTool({
  enabled,
}: ArticleMobileMapToolProps) {
  return (
    <React.Suspense
      fallback={
        <ArticleMobileMapToolContent
          enabled={enabled}
          requestedTrailCategory={null}
        />
      }
    >
      <ArticleMobileMapToolWithSearchParams enabled={enabled} />
    </React.Suspense>
  );
}

function ArticleMobileMapToolWithSearchParams({
  enabled,
}: ArticleMobileMapToolProps) {
  const searchParams = useSearchParams();
  return (
    <ArticleMobileMapToolContent
      enabled={enabled}
      requestedTrailCategory={searchParams.get(TRAIL_QUERY_PARAM)}
    />
  );
}

function ArticleMobileMapToolContent({
  enabled,
  requestedTrailCategory,
}: ArticleMobileMapToolProps & {
  requestedTrailCategory: string | null;
}) {
  const pathname = usePathname() ?? "";
  const mapTitle = getTrailMapLabel(pathname, requestedTrailCategory);
  const router = useRouter();
  const { closeSheet, openToolId } = useMobileBottomTools();
  const [pendingHref, setPendingHref] = React.useState<string | null>(null);

  const handleNavigate = React.useCallback(
    (href: string) => {
      setPendingHref(href);
      closeSheet();
    },
    [closeSheet],
  );

  React.useEffect(() => {
    if (!pendingHref || openToolId === "map") return;

    let frameId: number;
    const navigateAfterUnlock = () => {
      // 패널이 복원하는 이전 위치가 새 글의 스크롤 처리를 덮지 않도록 기다린다.
      if (document.documentElement.hasAttribute("data-scroll-locked")) {
        frameId = requestAnimationFrame(navigateAfterUnlock);
        return;
      }
      setPendingHref(null);
      router.push(pendingHref);
    };
    frameId = requestAnimationFrame(navigateAfterUnlock);
    return () => cancelAnimationFrame(frameId);
  }, [openToolId, pendingHref, router]);

  const mapContent = React.useMemo(
    () => (
      <div className="flex h-full min-h-0 flex-col">
        <TrailMap onNavigate={handleNavigate} />
      </div>
    ),
    [handleNavigate],
  );

  const tool = React.useMemo<MobileBottomToolItem>(
    () => ({
      id: "map",
      kind: "panel",
      label: "지도",
      icon: Map,
      order: 0,
      ariaLabel: `${mapTitle} 열기`,
      sheet: {
        title: mapTitle,
        resizable: true,
        defaultHeightRatio: 0.5,
        minHeightRatio: 0.3,
        maxHeightRatio: 0.85,
        content: mapContent,
      },
    }),
    [mapContent, mapTitle],
  );

  if (!enabled) return null;

  return <MobileBottomToolRegistration tool={tool} />;
}
